import { useMutation, useQueryClient } from '@tanstack/react-query'

import { toApiError, type ApiError } from '@/lib/api/errors'
import { saveCourtPriceDate } from '@/lib/venue/priceApi'
import type { CourtPriceDateSaveInput } from '@/lib/venue/priceTypes'
import type { BulkApplyResult, BulkApplyTarget } from '@/lib/venue/priceViewModel'

import { courtsQueryKey } from './useCourts'

export interface SavePriceDateVariables {
  date: string
  input: CourtPriceDateSaveInput
  /** 일괄 적용 대상(현재 코트 제외). 🚨 **그 날짜 하나만** 복제된다(설계 D3). */
  targets: BulkApplyTarget[]
  /** 현재 코트 이름 — 결과 보고 문구용. */
  courtName: string
}

export interface SavePriceDateOutcome {
  results: BulkApplyResult[]
}

/**
 * 날짜 지정 요금 저장 + 일괄 적용 팬아웃(#866 API ④, 설계 요구 1·2).
 *
 * 🚨 저장 응답을 소비하지 않는다 — 응답 형태가 아직 계약 문서로 확정되지 않아(설계 R1) 목록 쿼리를
 * **무효화**한다(요일유형 축이 응답을 캐시에 직접 꽂는 것과 의도적으로 다르다).
 * 실행 순서·부분 실패 처리는 요일유형 축(`useSaveCourtPriceBands`)과 같다.
 */
export function useSaveCourtPriceDate(venueId: string, courtId: number) {
  const queryClient = useQueryClient()

  return useMutation<SavePriceDateOutcome, ApiError, SavePriceDateVariables>({
    mutationFn: async ({ date, input, targets, courtName }) => {
      await saveCourtPriceDate(venueId, courtId, date, input)
      const results: BulkApplyResult[] = [{ courtId, name: courtName, ok: true }]

      for (const target of targets) {
        try {
          await saveCourtPriceDate(venueId, target.courtId, date, input)
          results.push({ ...target, ok: true })
        } catch (error) {
          results.push({ ...target, ok: false, message: toApiError(error).message })
        }
      }

      return { results }
    },
    onSuccess: () => {
      // 🔸 `courtsQueryKey(venueId)` 와 같은 접두라, 이 한 번으로 코트 목록·날짜 목록이 함께 무효화된다.
      void queryClient.invalidateQueries({ queryKey: courtsQueryKey(venueId) })
    },
  })
}
